/* eslint-disable react/display-name */
/* eslint-disable react/prop-types */
import { useState, forwardRef, useContext } from 'react';
import styled, { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import Menu from '@base/Menu/Menu';
import H6 from '@base/H6/H6';
import KeyboardArrowDown from '@icon/KeyboardArrowDown';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { Spacings, TextColors, Fonts, breakpoints } from '@styles/index';
import MenuItem from './MenuItem';

const DropDownButton = styled.button`
  display: flex;
  justify-content: space-between;
  align-self: center;
  align-items: center;
  line-height: 150%;
  cursor: pointer;
  transition: all 400ms ease-in-out;
  border-width: 1px;
  border-style: solid;
  font-weight: 600;
  border-radius: ${Spacings.SPACING_2B};
  background-color: ${({ theme }) => theme.colors.white};
  grid-column: ${({ gridColumn }) => gridColumn};
  padding: ${Spacings.SPACING_3B} ${Spacings.SPACING_4B};
  font-size: ${Fonts.SMALL};
  color: ${({ theme }) => theme.colors.fontGrey};
  box-shadow: ${({ theme }) => boxShadowGenerator({ theme }) || 'none'};
  border-color: ${({ showSelectedBorder, theme, open }) => {
    if (showSelectedBorder) return theme.colors.fontGrey;
    if (open) return theme.colors.fontLightBlue;
    return theme.colors.dropShadowV2;
  }};
  ${({ buttonWidth }) => buttonWidth && `width: ${buttonWidth};`}
  :hover {
    background-color: ${({ theme }) => theme.colors.white};
    border-color: ${({ showSelectedBorder, theme }) =>
      showSelectedBorder ? theme.colors.fontGrey : theme.colors.fontLightBlue};
    box-shadow: ${({ theme }) =>
      boxShadowGenerator({
        yOffset: Spacings.SPACING_2B,
        blur: Spacings.SPACING_2B,
        theme
      }) || 'none'};
  }
  @media (max-width: ${breakpoints.mobile}) {
    font-size: 12px;
    padding: ${Spacings.SPACING_2B} ${Spacings.SPACING_3B};
  }
`;

const Button = forwardRef(
  (
    {
      open,
      onClick,
      gridColumn,
      children,
      showSelectedBorder,
      className,
      buttonWidth
    },
    ref
  ) => (
    <DropDownButton
      open={open}
      onClick={onClick}
      gridColumn={gridColumn}
      ref={ref}
      showSelectedBorder={showSelectedBorder}
      className={className}
      type="button"
      buttonWidth={buttonWidth}
    >
      {children}
    </DropDownButton>
  )
);

const StyledArrowDown = styled(KeyboardArrowDown)`
  transition: transform 400ms ease-in-out;
  transform: ${({ open }) => (open ? 'rotate(180deg)' : 'rotate(0deg)')};
  @media only screen and (max-width: 47.95em) {
    margin-left: 8px;
  }
`;

const IconWrapper = styled.div`
  margin-right: ${({ icon }) => icon && Spacings.SPACING_2B};
  display: flex;
`;

const getFavoriteKey = (option) =>
  ['value', 'code', 'id', 'uid'].find((key) =>
    Object.keys(option || {}).some((k) => k === key)
  );

const selectedOptionHasValueOrId = (selectedOption) => {
  const favoriteKey = getFavoriteKey(selectedOption);
  if (selectedOption?.[favoriteKey]) return true;
  return false;
};

const isDefined = (value) => !(value === undefined || value === null);

const isSelected = (selectedOption, option) => {
  const favoriteKey = getFavoriteKey(selectedOption);
  if (
    isDefined(selectedOption?.[favoriteKey]) &&
    selectedOption?.[favoriteKey] === option[favoriteKey]
  )
    return true;
  return false;
};

const Dropdown = forwardRef((props, ref) => {
  const {
    options = [],
    selectedOption,
    onOptionClick,
    onClick,
    displayCheck,
    children,
    gridColumn,
    links,
    isDisabled,
    stripLength,
    showSelectedBorder,
    className,
    openDropDown,
    closeDropdown,
    state,
    useOuterState,
    displayIcon,
    menuWidth,
    buttonWidth,
    placeholder,
    menuColor,
    topChildren,
    showIcon,
    textColor
  } = props;
  const theme = useContext(ThemeContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const handleClick = (event) => {
    onClick();
    if (isDisabled) {
      return;
    }
    if (useOuterState) openDropDown(event.currentTarget);
    else setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    if (useOuterState) closeDropdown();
    else setAnchorEl(null);
  };
  const handleOptionClick = (option) => {
    onOptionClick(option);
    handleClose();
  };

  const stripLabel = (label) => {
    if (stripLength && label?.length > stripLength)
      return `${label.substr(0, stripLength)} ...`;
    return label;
  };

  const stateOrAnchor = state || anchorEl;

  return (
    <>
      <Button
        open={stateOrAnchor}
        onClick={handleClick}
        gridColumn={gridColumn}
        ref={ref}
        showSelectedBorder={
          showSelectedBorder && selectedOptionHasValueOrId(selectedOption)
        }
        className={className}
        buttonWidth={buttonWidth}
      >
        {displayIcon ? (
          <IconWrapper icon={displayIcon}>{displayIcon}</IconWrapper>
        ) : null}
        {selectedOption ? (
          stripLabel(
            selectedOption.label ||
              selectedOption.name ||
              selectedOption.display_name ||
              'No preference'
          )
        ) : (
          <H6 color={textColor || TextColors.SECONDARY}>{placeholder}</H6>
        )}
        <StyledArrowDown
          open={stateOrAnchor}
          size="16px"
          color={theme.colors.fontGrey}
        />
      </Button>
      <Menu anchorEl={stateOrAnchor} onClose={handleClose} width={menuWidth}>
        {topChildren}
        {options.map((option) => (
          <MenuItem
            displayCheck={displayCheck}
            selected={isSelected(selectedOption, option)}
            option={option}
            onClick={handleOptionClick}
            key={option.value || option.id}
            links={links}
            color={menuColor}
            showIcon={showIcon}
          />
        ))}
        {children}
      </Menu>
    </>
  );
});

export default Dropdown;

Dropdown.propTypes = {
  selectedOption: PropTypes.objectOf(PropTypes.any),
  options: PropTypes.arrayOf(PropTypes.any),
  onOptionClick: PropTypes.func.isRequired,
  displayCheck: PropTypes.bool,
  children: PropTypes.node,
  gridColumn: PropTypes.string,
  links: PropTypes.bool,
  stripLength: PropTypes.number,
  showSelectedBorder: PropTypes.bool,
  displayIcon: PropTypes.node,
  onClick: PropTypes.func,
  menuWidth: PropTypes.string,
  buttonWidth: PropTypes.string,
  placeholder: PropTypes.string,
  showIcon: PropTypes.bool
};

Dropdown.defaultProps = {
  selectedOption: null,
  options: [],
  displayCheck: false,
  showIcon: false,
  children: null,
  gridColumn: 'span 4',
  links: false,
  stripLength: null,
  showSelectedBorder: true,
  displayIcon: null,
  menuWidth: '233px',
  buttonWidth: null,
  onClick: () => {},
  placeholder: ''
};
